import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUser } from "./UserContext";
import {
  createNotification,
  CreateNotificationDto,
  fetchNotificationCount,
  fetchNotifications,
  INotification,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../api/NotificationApi";

export interface INotificationContext {
  notifications: INotification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  limit: number;
  refreshNotifications: () => Promise<void>;
  loadMore: () => Promise<void>;
  resetLimit: () => Promise<void>;
  markLoadedAsRead: () => Promise<void>;
  sendNotification: (
    message: string,
    sender?: string,
    extra?: Partial<CreateNotificationDto>,
  ) => Promise<INotification | null>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const DEFAULT_LIMIT = 25;
const LIMIT_STEP = 25;

const NotificationContext = createContext<INotificationContext>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  loadingMore: false,
  hasMore: true,
  limit: DEFAULT_LIMIT,
  refreshNotifications: async () => {},
  loadMore: async () => {},
  resetLimit: async () => {},
  markLoadedAsRead: async () => {},
  sendNotification: async () => null,
  markAsRead: async () => {},
  markAllAsRead: async () => {},
});

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useUser();
  const currentUserId = user?.user?.id;

  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const limitRef = useRef<number>(limit);
  useEffect(() => {
    limitRef.current = limit;
  }, [limit]);

  const markingIdsRef = useRef<Set<number>>(new Set());

  const refreshNotifications = useCallback(async () => {
    if (!currentUserId) return;
    try {
      setLoading(true);
      const curLimit = limitRef.current;
      const [list, countData] = await Promise.all([
        fetchNotifications(curLimit),
        fetchNotificationCount(currentUserId),
      ]);
      setNotifications(list);
      setUnreadCount(countData?.unreadCount ?? countData?.count ?? 0);
      setHasMore(list.length >= curLimit);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Mark all unread notifications currently loaded within limit as read
  const markLoadedAsRead = useCallback(async () => {
    if (!currentUserId) return;

    const unreadItems = notifications.filter((n) => {
      const readList = Array.isArray(n.readBy) ? n.readBy.map(Number) : [];
      return (
        !readList.includes(Number(currentUserId)) &&
        !markingIdsRef.current.has(n.id)
      );
    });

    if (unreadItems.length === 0) return;

    const unreadIds = unreadItems.map((n) => n.id);
    unreadIds.forEach((id) => markingIdsRef.current.add(id));

    // Optimistically update notifications state
    setNotifications((prev) =>
      prev.map((n) =>
        unreadIds.includes(n.id)
          ? {
              ...n,
              readBy: Array.from(
                new Set([
                  ...(n.readBy || []).map(Number),
                  Number(currentUserId),
                ]),
              ),
            }
          : n,
      ),
    );

    // Optimistically decrement unread count
    setUnreadCount((prev) => Math.max(0, prev - unreadIds.length));

    try {
      await Promise.all(
        unreadIds.map((id) => markNotificationAsRead(id, currentUserId)),
      );
      const countData = await fetchNotificationCount(currentUserId);
      setUnreadCount(countData?.unreadCount ?? countData?.count ?? 0);
    } catch (error) {
      console.error("Failed to mark loaded notifications as read:", error);
    } finally {
      unreadIds.forEach((id) => markingIdsRef.current.delete(id));
    }
  }, [currentUserId, notifications]);

  // Load more notifications and immediately mark any newly loaded unread ones as read
  const loadMore = useCallback(async () => {
    if (!currentUserId || loadingMore || !hasMore) return;
    const nextLimit = limit + LIMIT_STEP;
    try {
      setLoadingMore(true);
      const list = await fetchNotifications(nextLimit);
      setNotifications(list);
      setLimit(nextLimit);
      limitRef.current = nextLimit;
      setHasMore(list.length >= nextLimit);

      // Find any unread items in the newly fetched list
      const unreadItems = list.filter((n) => {
        const readList = Array.isArray(n.readBy) ? n.readBy.map(Number) : [];
        return (
          !readList.includes(Number(currentUserId)) &&
          !markingIdsRef.current.has(n.id)
        );
      });

      if (unreadItems.length > 0) {
        const unreadIds = unreadItems.map((n) => n.id);
        unreadIds.forEach((id) => markingIdsRef.current.add(id));

        // Optimistically update state
        setNotifications((prev) =>
          prev.map((n) =>
            unreadIds.includes(n.id)
              ? {
                  ...n,
                  readBy: Array.from(
                    new Set([
                      ...(n.readBy || []).map(Number),
                      Number(currentUserId),
                    ]),
                  ),
                }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - unreadIds.length));

        try {
          await Promise.all(
            unreadIds.map((id) => markNotificationAsRead(id, currentUserId)),
          );
          const countData = await fetchNotificationCount(currentUserId);
          setUnreadCount(countData?.unreadCount ?? countData?.count ?? 0);
        } catch (err) {
          console.error("Failed to mark expanded notifications as read:", err);
        } finally {
          unreadIds.forEach((id) => markingIdsRef.current.delete(id));
        }
      }
    } catch (error) {
      console.error("Failed to load more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentUserId, loadingMore, hasMore, limit]);

  const resetLimit = useCallback(async () => {
    setLimit(DEFAULT_LIMIT);
    limitRef.current = DEFAULT_LIMIT;
    setHasMore(true);
    if (!currentUserId) return;
    try {
      const [list, countData] = await Promise.all([
        fetchNotifications(DEFAULT_LIMIT),
        fetchNotificationCount(currentUserId),
      ]);
      setNotifications(list);
      setUnreadCount(countData?.unreadCount ?? countData?.count ?? 0);
      setHasMore(list.length >= DEFAULT_LIMIT);
    } catch (error) {
      console.error("Failed to reset notifications:", error);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) {
      refreshNotifications();

      // Poll periodically (every 30 seconds)
      const interval = setInterval(() => {
        refreshNotifications();
      }, 30000);

      return () => clearInterval(interval);
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setLimit(DEFAULT_LIMIT);
      limitRef.current = DEFAULT_LIMIT;
      setHasMore(true);
    }
  }, [currentUserId, refreshNotifications]);

  const sendNotification = useCallback(
    async (
      message: string,
      sender?: string,
      extra?: Partial<CreateNotificationDto>,
    ): Promise<INotification | null> => {
      try {
        const payload: CreateNotificationDto = {
          message,
          sender: sender || user?.user?.userName || "System",
          readBy: [],
          ...extra,
        };
        const created = await createNotification(payload);
        await refreshNotifications();
        return created;
      } catch (error) {
        console.error("Failed to send notification:", error);
        return null;
      }
    },
    [user?.user?.userName, refreshNotifications],
  );

  const markAsRead = useCallback(
    async (id: number) => {
      if (!currentUserId) return;
      try {
        // Optimistic UI update
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === id
              ? {
                  ...n,
                  readBy: Array.from(
                    new Set([
                      ...(n.readBy || []).map(Number),
                      Number(currentUserId),
                    ]),
                  ),
                }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        await markNotificationAsRead(id, currentUserId);
        await refreshNotifications();
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [currentUserId, refreshNotifications],
  );

  const markAllAsRead = useCallback(async () => {
    if (!currentUserId) return;
    try {
      // Optimistic UI update
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readBy: Array.from(
            new Set([...(n.readBy || []).map(Number), Number(currentUserId)]),
          ),
        })),
      );
      setUnreadCount(0);

      await markAllNotificationsAsRead(currentUserId);
      await refreshNotifications();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, [currentUserId, refreshNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        loadingMore,
        hasMore,
        limit,
        refreshNotifications,
        loadMore,
        resetLimit,
        markLoadedAsRead,
        sendNotification,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
